import { VIDEO_CHANNEL_OPTIONS } from "../constants"

export const FRAME_COPY_STALE_SOURCE_ORDER = [
  "video",
  "product",
  "model",
  "frame",
] as const

export type FrameCopyStaleSource =
  (typeof FRAME_COPY_STALE_SOURCE_ORDER)[number]

export type FrameCopyFrontendRemark = {
  channel?: API.Task.CreateVideoChannel
  frame_applicable_confirmed?: boolean
  stale_sources?: FrameCopyStaleSource[]
  [key: string]: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

export const normalizeFrameCopyStaleSources = (
  value: unknown,
): FrameCopyStaleSource[] => {
  if (!Array.isArray(value)) return []

  const sources = new Set(
    value.filter((source): source is FrameCopyStaleSource =>
      FRAME_COPY_STALE_SOURCE_ORDER.includes(source as FrameCopyStaleSource),
    ),
  )

  return FRAME_COPY_STALE_SOURCE_ORDER.filter((source) => sources.has(source))
}

export const parseFrameCopyFrontendRemark = (
  remark?: string | null,
): FrameCopyFrontendRemark => {
  if (!remark) return {}

  try {
    const value: unknown = JSON.parse(remark)

    if (!isRecord(value)) return {}

    const staleSources = normalizeFrameCopyStaleSources(value.stale_sources)
    const parsed: FrameCopyFrontendRemark = { ...value }
    parsed.frame_applicable_confirmed =
      value.frame_applicable_confirmed === true

    if (staleSources.length) {
      parsed.stale_sources = staleSources
    } else {
      delete parsed.stale_sources
    }

    return parsed
  } catch {
    return {}
  }
}

export const serializeFrameCopyFrontendRemark = (
  remark: FrameCopyFrontendRemark,
) => {
  const value: FrameCopyFrontendRemark = { ...remark }
  const staleSources = normalizeFrameCopyStaleSources(value.stale_sources)

  if (staleSources.length) {
    value.stale_sources = staleSources
  } else {
    delete value.stale_sources
  }

  return JSON.stringify(value)
}

export const createVideoChannelFrontendRemark = (
  channel: API.Task.CreateVideoChannel,
) => {
  return JSON.stringify({ channel })
}

export const getVideoChannelFromFrontendRemark = (remark?: string | null) => {
  if (!remark) return undefined

  try {
    const value = JSON.parse(remark) as { channel?: unknown }

    return VIDEO_CHANNEL_OPTIONS.find(
      (option) => option.value === value.channel,
    )?.value
  } catch {
    return undefined
  }
}

type TaskVideoChannelSource = {
  create_video?: {
    channel: API.Task.CreateVideoChannel
  } | null
  frontend_remark?: string | null
}

export const getTaskVideoChannel = (task: TaskVideoChannelSource) => {
  return (
    getVideoChannelFromFrontendRemark(task.frontend_remark) ??
    task.create_video?.channel
  )
}
